
import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/components/ui/use-toast";
import { authFormSchema, type AuthFormValues } from "@/schemas/authSchema";
import { SocialLoginOptions } from "@/components/auth/SocialLoginOptions";
import { useNavigate } from "react-router-dom";
import { MobileAuthModeToggle } from "./MobileAuthModeToggle";
import { MobileAuthFormFields } from "./MobileAuthFormFields";

interface MobileAuthFormProps {
  mode: "signin" | "signup";
  onToggleMode: () => void;
  onForgotPassword: () => void;
}

export function MobileAuthForm({ mode, onToggleMode, onForgotPassword }: MobileAuthFormProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const { toast } = useToast();
  const navigate = useNavigate();

  const form = useForm<AuthFormValues>({
    resolver: zodResolver(authFormSchema),
    defaultValues: {
      name: "",
      email: "",
      password: "",
    },
  });

  async function onSubmit(formData: AuthFormValues) {
    setIsLoading(true);
    
    try {
      if (mode === "signup") {
        const { error } = await supabase.auth.signUp({
          email: formData.email,
          password: formData.password,
          options: {
            data: {
              full_name: formData.name,
            },
            emailRedirectTo: `${window.location.origin}/dashboard`
          },
        });
        
        if (error) throw error;
        
        toast({
          title: "Check your email",
          description: "We've sent you a verification link",
        });
      } else {
        const { error } = await supabase.auth.signInWithPassword({
          email: formData.email,
          password: formData.password,
        });
        
        if (error) throw error;
        
        navigate("/dashboard");
      }
    } catch (error: any) {
      toast({
        title: mode === "signup" ? "Sign Up Error" : "Sign In Error",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <div className="w-full max-w-sm mx-auto space-y-6">
      <MobileAuthModeToggle mode={mode} onToggleMode={onToggleMode} />

      <SocialLoginOptions />
      
      <div className="relative">
        <div className="absolute inset-0 flex items-center">
          <span className="w-full border-t" />
        </div>
        <div className="relative flex justify-center text-xs uppercase">
          <span className="bg-background px-2 text-muted-foreground">Or continue with</span>
        </div>
      </div>

      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
        <MobileAuthFormFields
          mode={mode}
          showPassword={showPassword}
          onTogglePassword={() => setShowPassword(!showPassword)}
          onForgotPassword={onForgotPassword}
          register={form.register}
          errors={form.formState.errors}
          isLoading={isLoading}
        />
      </form>

      {mode === "signup" && (
        <p className="text-xs text-center text-muted-foreground px-4">
          By signing up, you agree to our{" "}
          <a href="/privacy-terms" className="underline hover:text-primary">
            Terms of Service and Privacy Policy
          </a>
        </p>
      )}
    </div>
  );
}
